<script lang="ts" setup>
// ================================
// IMPORTS
// ================================

// Vue
import { ref, onMounted, onBeforeUnmount, inject } from "vue"
import { useI18n } from "vue-i18n"
import moment from "moment"

// VueUse
import { useEventBus } from "@vueuse/core"

// Composables
import { useFetchWrapper } from "@core/composables"

// Constants

// Types
import type { ConsentementLegacy } from "@types"
import type { Services } from "@services"

// Consent
import {
    CONSENT_EXTEND_DEFAULT_MONTHS,
    CONSENT_PAGE_AFTER_SOURCE_MULTI_APPEND_BUS_KEY,
    type ConsentListPatch,
} from "./consent.events"

// ================================
// INTERNATIONALIZATION
// ================================

const { t } = useI18n({
    messages: {
        fr: {
            title: "Consentements",
            // Filter groups
            filter_type: "Type",
            filter_owner: "Titulaire",
            filter_status: "Avancement",
            filter_grdf_access: "Accès GRDF",
            // Type items
            type_pdl: "PDL",
            type_pce: "PCE",
            // Owner items
            owner_vous: "Vous",
            owner_personne_physique: "Personne physique",
            owner_personne_morale: "Personne morale",
            // Status items
            status_actif: "Actif",
            status_expire: "Expiré",
            status_revoque: "Révoqué",
            // GRDF Access items
            grdf_access_actif: "Actif",
            grdf_access_verification_grdf: "Vérification GRDF",
            grdf_access_obsolete: "Obsolète",
            grdf_access_a_valider: "À valider",
            grdf_access_refuse: "Refusée",
            grdf_access_erreur: "Erreur",
            grdf_access_revoquee: "Révoquée",
            grdf_access_non_declare: "Non déclaré",
        },
    },
})

// ================================
// COMPOSABLES
// ================================
/**
 * Service de gestion des consentements
 */
const { $getConsents } = inject<Services>("services")!

// ================================
// STATE
// ================================
/**
 * Nombre de lignes par page dans le tableau
 */
const rowsPerPage = ref<number>(10)

/**
 * Liste des consentements chargés depuis l'API
 */
const consents = ref<ConsentementLegacy[] | null>(null)

/**
 * Wrapper de récupération des consentements avec gestion d'état
 */
const {
    fetch: fetchConsent,
    isFetching: isFetchingConsentRequests,
    hasError: hasErrorConsentRequests,
    error: errorConsentRequests,
} = useFetchWrapper(async () => {
    const result = await $getConsents()
    consents.value = result
})

const busAfterSourceMultiAppend = useEventBus<void>(
    CONSENT_PAGE_AFTER_SOURCE_MULTI_APPEND_BUS_KEY
)

const unsubscribeAfterSourceMultiAppend = busAfterSourceMultiAppend.on(() => {
    void fetchConsent()
})

// ================================
// LIFECYCLE
// ================================
/**
 * Charge les consentements au montage du composant
 */
onMounted(() => {
    fetchConsent()
})

/**
 * Nettoie l'écouteur d'événements avant la destruction du composant
 */
onBeforeUnmount(() => {
    unsubscribeAfterSourceMultiAppend()
})

/**
 * Met à jour la liste des consentements en local (révocation, prolongement signé) sans refetch.
 */
function applyConsentListPatch(patch: ConsentListPatch): void {
    if (!consents.value) return

    if (patch.kind === "revoked") {
        const idx = consents.value.findIndex((c) => c._id === patch.consentId)
        if (idx === -1) return
        const prev = consents.value[idx]
        const now = new Date().toISOString()
        const next: ConsentementLegacy = {
            ...prev,
            dismissed: true,
            dismissed_at: now,
            updatedAt: now,
        }
        consents.value = [
            ...consents.value.slice(0, idx),
            next,
            ...consents.value.slice(idx + 1),
        ]
        return
    }

    const idx = consents.value.findIndex(
        (c) => c._id === patch.previousConsentId
    )
    if (idx === -1) return
    const prev = consents.value[idx]
    const f = patch.fields
    const newId = patch.newConsentId ?? f?._id ?? prev._id
    const extendedDateTo =
        f?.date_to ??
        (prev.date_to && moment(prev.date_to).isValid()
            ? moment(prev.date_to)
                  .add(CONSENT_EXTEND_DEFAULT_MONTHS, "months")
                  .toISOString()
            : prev.date_to)
    const now = new Date().toISOString()
    const next: ConsentementLegacy = {
        ...prev,
        _id: newId,
        dismissed: false,
        dismissed_at: null,
        signed: true,
        date_to: extendedDateTo,
        date_from: f?.date_from ?? prev.date_from,
        date_from_data: f?.date_from_data ?? prev.date_from_data,
        signed_at: f?.signed_at ?? prev.signed_at,
        updatedAt: f?.updatedAt ?? now,
        createdAt: f?.createdAt ?? prev.createdAt,
    }
    consents.value = [
        ...consents.value.slice(0, idx),
        next,
        ...consents.value.slice(idx + 1),
    ]
}
</script>

<template>
    <BaseTitledCard class="consent-card w-full flex flex-col">
        <template #header>
            {{ t("title") }}
        </template>
        <!-- ================================ -->
        <!-- CONTENU PRINCIPAL -->
        <!-- ================================ -->
        <template #content>
            <div class="flex flex-col gap-12">
                <!-- ================================ -->
                <!-- ÉTAT DE CHARGEMENT -->
                <!-- ================================ -->
                <BaseSkeleton
                    v-if="isFetchingConsentRequests"
                    width="100%"
                    height="30rem"
                    class="mx-auto"
                />

                <!-- ================================ -->
                <!-- ÉTAT D'ERREUR -->
                <!-- ================================ -->
                <BaseMessage
                    severity="error"
                    v-else-if="hasErrorConsentRequests"
                >
                    {{
                        errorConsentRequests?.message ||
                        "Une erreur est survenue lors du chargement de la demande"
                    }}
                </BaseMessage>

                <!-- ================================ -->
                <!-- TABLEAU DES CONSENTEMENTS -->
                <!-- ================================ -->
                <ConsentTable
                    v-else-if="consents?.length"
                    :consents="consents"
                    :rows-per-page="rowsPerPage"
                    @patch-consent-list="applyConsentListPatch"
                />

                <!-- ================================ -->
                <!-- ÉTAT VIDE -->
                <!-- ================================ -->
                <BaseMessage v-else>
                    Aucune demande de consentement trouvée.
                </BaseMessage>
            </div>
        </template>
    </BaseTitledCard>
</template>

<style>
/* ================================ */
/* PANEL DE FILTRES SANS SCROLL */
/* ================================ */
.filter-panel-no-scroll .p-multiselect-items-wrapper {
    max-height: none !important;
    overflow: visible !important;
}

.filter-panel-no-scroll .p-multiselect-items {
    max-height: none !important;
}

.filter-panel-no-scroll {
    min-width: 300px;
}
</style>
